import { useContext, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { inject, observer, PropTypes as MobXPropTypes } from 'mobx-react';
import Table from 'react-bootstrap/Table';
import queryString from 'query-string';
import BalancesDocumentsFiltersBar from './components/BalancesDocumentsFiltersBar';
import BalancesDocumentsTableHeadRow from './components/BalancesDocumentsTableHeadRow';
import BalancesDocumentsTableRowContent from './components/BalancesDocumentsTableRowContent';
import { DIRECTION } from 'components/constants';
import EmptyTableContentPlug from 'components/EmptyTableContentPlug';
import Loader from 'components/Loader';
import i18nContext from 'components/i18n-context';
import Pagination from 'components/Pagination/Pagination';
import './BalancesDocumentsTable.scss';

const BalancesDocumentsTable = ({ balancesStore }) => {
  const i18n = useContext(i18nContext);
  const location = useLocation();
  const navigate = useNavigate();

  const updateFiltersInUrl = (filters, pagination, sortColumn) => {
    const filtersParams = balancesStore.prepareFiltersParams();
    const queryParams = {
      ...filtersParams,
      page: pagination.page,
      size: pagination.size,
      sort_column: sortColumn.sort_column,
      sort_direction: sortColumn.sort_direction
    };
    const searchParams = queryString.stringify(queryParams);
    navigate({ search: `?${searchParams}` }, { replace: true });
  };

  const loadFiltersFromUrl = () => {
    const params = queryString.parse(location.search);
    balancesStore.setFiltersFromUrl(params);
  };

  useEffect(() => {
    loadFiltersFromUrl();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!balancesStore.isInitialized) {
      balancesStore.getBalancesDocumentsList();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [balancesStore.isInitialized]);

  useEffect(() => {
    if (balancesStore.isInitialized) {
      balancesStore.resetBalancesStore();
      loadFiltersFromUrl();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  useEffect(() => {
    updateFiltersInUrl(balancesStore.filters, balancesStore.pagination, balancesStore.sortColumn);
    if (balancesStore.isInitialized) {
      balancesStore.getBalancesDocumentsList();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    balancesStore.filters.date,
    balancesStore.pagination.size,
    balancesStore.pagination.page,
    balancesStore.sortColumn.sort_column,
    balancesStore.sortColumn.sort_direction
  ]);

  const handleOnSortTable = (newSortBy) => {
    balancesStore.setSortData({
      sortBy: newSortBy,
      direction:
        balancesStore.sortColumn.sort_column !== newSortBy
          ? DIRECTION.ASC
          : balancesStore.sortColumn.sort_direction === DIRECTION.ASC
            ? DIRECTION.DESC
            : DIRECTION.ASC
    });
  };

  if (!balancesStore.isInitialized) {
    return <Loader className={'application-loader'} />;
  }

  return (
    <div className={'balances-documents-wrapper'}>
      <BalancesDocumentsFiltersBar filters={balancesStore.filters} handleSetFilters={balancesStore.setFilter} />

      {balancesStore.balancesDocumentsList?.length === 0 ? (
        <EmptyTableContentPlug
          headerText={i18n.getMessage('balances.balancesDocuments.table.emptyPlug.header')}
          descriptionText={i18n.getMessage('balances.balancesDocuments.table.emptyPlug.description')}
        />
      ) : (
        <>
          <Pagination
            isShowPaginationSize={true}
            paginationSize={balancesStore.pagination.size}
            handleChangePaginationSize={balancesStore.setPageSize}
            isLoading={balancesStore.isLoading}
            currentPage={balancesStore.pagination.page}
            numberOfPages={balancesStore.pagination.totalPages}
            setCurrentPage={balancesStore.setPageNumber}
          />

          <Table responsive>
            <thead>
              <BalancesDocumentsTableHeadRow
                handleOnSortTable={handleOnSortTable}
                sortBy={balancesStore.sortColumn.sort_column}
                direction={balancesStore.sortColumn.sort_direction}
              />
            </thead>
            <tbody>
              {balancesStore.balancesDocumentsList &&
                balancesStore.balancesDocumentsList.length > 0 &&
                balancesStore.balancesDocumentsList.map((balancesDocument, index) => (
                  <BalancesDocumentsTableRowContent
                    key={index}
                    type={'list'}
                    isLoading={balancesStore.isLoading}
                    balancesDocumentData={balancesDocument}
                    handleDownloadBalancesDocumentById={balancesStore.downloadBalancesDocumentById}
                  />
                ))}
            </tbody>
          </Table>

          <Pagination
            isShowPaginationSize={true}
            paginationSize={balancesStore.pagination.size}
            handleChangePaginationSize={balancesStore.setPageSize}
            isLoading={balancesStore.isLoading}
            currentPage={balancesStore.pagination.page}
            numberOfPages={balancesStore.pagination.totalPages}
            setCurrentPage={balancesStore.setPageNumber}
          />
        </>
      )}
    </div>
  );
};

BalancesDocumentsTable.propTypes = {
  balancesStore: MobXPropTypes.observableObject
};

export default inject((stores) => ({
  balancesStore: stores.balancesStore
}))(observer(BalancesDocumentsTable));
